/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Plumbing Contractors",
  title: "AI for plumbing contractors",
  titleHighlight: "for service, rough-in, and repipes.",
  description: `One platform where AI helps plumbing contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for plumbing contractors",
    items: [
      {
        icon: Sparkles,
        title: "Fixture database",
        description:
          "Toilets, sinks, water heaters, valves — tracked with current supplier pricing.",
      },
      {
        icon: Sparkles,
        title: "Rough-in line items",
        description:
          "Draw pipe runs and count fittings on the mechanical drawing; AI matches each to your cost database.",
      },
      {
        icon: Sparkles,
        title: "Repipe estimating",
        description:
          "Whole-house repipes with labor variance factors for crawl space vs. slab.",
      },
      {
        icon: Sparkles,
        title: "Service calls",
        description:
          "Dispatch-light workflow for one-off service work alongside new construction.",
      },
      {
        icon: Sparkles,
        title: "Code compliance flags",
        description:
          "Track required backflow preventers, shut-offs, and other code items per job.",
      },
      {
        icon: Sparkles,
        title: "Hank for plumbers",
        description:
          "'What's our average margin on water heater swaps?' Real answer from real jobs.",
      },
    ],
  },

  workflow: {
    title: "How it works for plumbing contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Bid",
        title: "Fixtures, pipe, and labor",
        description:
          "Draw pipe runs and count fixtures on the drawing, then AI matches each line to your cost database. Hank flags missing labor or required code items before the bid ships. Branded proposals in one click.",
        details: [],
      },
      {
        label: "Rough-in",
        title: "Project-level visibility",
        description:
          "Accepted bid becomes a project with crew assignments, fixture orders, and inspection tracking. Know who's on what job and when each phase is due.",
        details: [],
      },
      {
        label: "Service",
        title: "Same system, different workflow",
        description:
          "Service calls flow through the same database as new construction, so a plumber who does both has one customer history and one billing pipeline.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Rough-in, finish, and service in one pipeline",
      description:
        "New construction, repipes, and service calls all live in the same workspace. A plumbing company that does all three doesn't need three tools or three separate customer records — everything is in one platform.",
      bullets: [
        "Unified rough-in and service workflows",
        "Customer history across job types",
        "Fixture tracking per property",
        "Different labor rates per work type",
      ],
    },
    {
      icon: Sparkles,
      title: "Fixture database tied to real supplier prices",
      description:
        "Fixture pricing is volatile — especially water heaters, tankless units, and specialty valves. Contractor Co-Pilot lets you import supplier catalogs and maintain pricing per vendor so your estimates reflect reality.",
      bullets: [
        "Per-vendor fixture pricing",
        "Supplier catalog imports from PDF",
        "Margin tracking per fixture type",
        "Vendor comparison per line item",
      ],
    },
    {
      icon: Sparkles,
      title: "Labor variance for crawl space, slab, and basement",
      description:
        "A rough-in in an open basement is fundamentally different from a rough-in under a slab. Variance factors adjust labor production rates per install environment so your estimates stop losing money on hard-access jobs.",
      bullets: [
        "Variance factors by access difficulty",
        "Separate labor rates per condition",
        "Production rate tracking from real jobs",
        "Estimate audit flags for missing variance",
      ],
    },
    {
      icon: Sparkles,
      title: "Audit every estimate before it ships",
      description:
        "Plumbing jobs fail when a code item is missed — backflow preventer, expansion tank, shut-off. Hank audits estimates against similar past jobs and flags what's missing before the bid goes out.",
      bullets: [
        "Missing line detection",
        "Code compliance warnings",
        "Historical job comparison",
        "Plain-English audit summaries",
      ],
    },
  ],

  relatedFeatures: [
    { label: "Plumbing Solutions", href: "/solutions/plumbing", icon: Ruler },
    {
      label: "Plumbing Calculator",
      href: "/tools/plumbing-calculator",
      icon: Calculator,
    },
    {
      label: "How to Estimate Plumbing Jobs",
      href: "/blog/how-to-estimate-plumbing-jobs",
      icon: BookOpen,
    },
    {
      label: "CC vs Spreadsheets",
      href: "/compare/spreadsheets",
      icon: BarChart3,
    },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    { label: "Blueprint Takeoffs", href: "/features/takeoffs", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForPlumbingContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
